import { observable, action, computed, decorate } from "mobx";

/*
 * "What's new" on the right rail. Product updates are WordPress posts in the
 * "Product updates" category (id 70) on salesgear.io -- the changelog agent
 * drafts them, a person publishes them, and the app just reads the public REST
 * feed (CORS already allows app.salesgear.io). Seen-state is per browser: the
 * date of the newest post the user has opened the panel on.
 */
const FEED =
  "https://www.salesgear.io/wp-json/wp/v2/posts?categories=70&per_page=30&_embed=wp:term,wp:featuredmedia" +
  "&_fields=id,date,title,excerpt,content,link,_links,_embedded";
// Post tags the changelog agent sets; anything else is ignored.
const TYPES = ["New feature", "Improvement", "Fix"];
export const ALL_UPDATES_URL = "https://www.salesgear.io/blog/category/product-updates/";
const SEEN_KEY = "sg_updates_seen";
const FIRST_VISIT_NEW_DAYS = 14;
const REFRESH_MIN_MS = 10 * 60 * 1000;
export const REFRESH_EVERY_MS = 15 * 60 * 1000;
const APP_PATH = /href="https:\/\/app\.salesgear\.io(\/[^"]*)"/;

const parse = html => new DOMParser().parseFromString(html || "", "text/html").body;
const text = html => parse(html).textContent.trim();
// The "What changed" bullets of a post, as plain text (never injected as HTML).
const points = html =>
  [...parse(html).querySelectorAll("li")].map(li => li.textContent.trim()).filter(Boolean);

// Label for the post, from where its "Try it" link lands in the app.
const AREAS = [
  ["/copilot", "Copilot"],
  ["/agents", "Agents"],
  ["/sequences", "Sequences"],
  ["/contacts", "Contacts"],
  ["/accounts", "Accounts"],
  ["/list-builder", "Prospecting"],
  ["/deep-research", "Research"],
  ["/data-toolkit", "Data"],
  ["/settings/imports", "Imports"],
  ["/inbox", "Inbox"],
  ["/reports", "Reports"],
  ["/pricing", "Billing"],
  ["/settings", "Settings"]
];
const areaOf = path => (AREAS.find(([prefix]) => path.startsWith(prefix)) || [])[1] || "Update";

const typeOf = p => {
  const names = (p._embedded?.["wp:term"] || []).flat().map(t => t?.name);
  return TYPES.find(t => names.includes(t)) || "";
};

// A mid-size rendition when WordPress made one; the panel never needs the original.
const imageOf = p => {
  const media = (p._embedded?.["wp:featuredmedia"] || [])[0];
  const sizes = media?.media_details?.sizes || {};
  return (sizes.medium_large || sizes.large || sizes.medium || {}).source_url || media?.source_url || "";
};

const readSeen = () => {
  try {
    return localStorage.getItem(SEEN_KEY) || "";
  } catch (e) {
    return "";
  }
};

class UpdatesPanelStore {
  open = false;
  posts = [];
  loading = false;
  failed = false;
  seen = readSeen();
  loadPromise = null;

  lastFetchedAt = 0;

  get unseenCount() {
    return this.posts.filter(p => p.date > this.seen).length;
  }

  // Once per session; the rail calls it after the page settles so it never
  // competes with the first screen's own requests.
  load = () => {
    if (!this.loadPromise) {
      this.loading = true;
      this.failed = false;
      this.lastFetchedAt = Date.now();
      this.loadPromise = fetch(FEED)
        .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
        .then(this.setPosts)
        .catch(this.setFailed);
    }
    return this.loadPromise;
  };

  // The app stays open for hours, so an update published mid-session would
  // otherwise only show after a reload. Quiet re-check (no skeletons) when the
  // tab regains focus and on a slow timer; at most once every 10 minutes.
  refresh = () => {
    if (!this.loadPromise || this.loading) return;
    if (Date.now() - this.lastFetchedAt < REFRESH_MIN_MS) return;
    this.lastFetchedAt = Date.now();
    fetch(FEED)
      .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
      .then(this.setPosts)
      .catch(() => {
        /* keep what we have; the next refresh tries again */
      });
  };

  retry = () => {
    this.loadPromise = null;
    return this.load();
  };

  setPosts = rows => {
    this.posts = (rows || []).map(p => ({
      id: p.id,
      date: p.date,
      title: text(p.title?.rendered),
      summary: text(p.excerpt?.rendered),
      path: ((p.content?.rendered || "").match(APP_PATH) || [])[1] || "",
      points: points(p.content?.rendered),
      link: p.link,
      type: typeOf(p),
      image: imageOf(p)
    })).map(p => ({ ...p, area: areaOf(p.path) }));
    this.loading = false;
    // First visit: only the last two weeks count as new, not the whole backlog ("9+").
    if (!this.seen && this.posts.length) {
      const cutoff = new Date(Date.now() - FIRST_VISIT_NEW_DAYS * 86400000).toISOString();
      const older = this.posts.map(p => p.date).filter(d => d < cutoff).sort();
      if (older.length) {
        this.seen = older[older.length - 1];
        try {
          localStorage.setItem(SEEN_KEY, this.seen);
        } catch (e) {}
      }
    }
  };

  setFailed = () => {
    this.loading = false;
    this.failed = true;
  };

  markSeen = () => {
    const newest = this.posts[0]?.date;
    if (!newest || newest <= this.seen) return;
    this.seen = newest;
    try {
      localStorage.setItem(SEEN_KEY, newest);
    } catch (e) {
      // Private window or blocked storage: the dot just comes back next session.
    }
  };

  toggle = () => {
    this.open = !this.open;
  };

  close = () => {
    this.open = false;
  };
}

decorate(UpdatesPanelStore, {
  open: observable,
  posts: observable.ref,
  loading: observable,
  failed: observable,
  seen: observable,
  unseenCount: computed,
  setPosts: action,
  setFailed: action,
  load: action,
  markSeen: action,
  toggle: action,
  close: action
});

const updatesPanelStore = new UpdatesPanelStore();
export default updatesPanelStore;
